<template>
  <el-dialog
    :visible="visible"
    width="900px"
    :close-on-click-modal="false"
    append-to-body
    top="5vh"
    @closed="handleClosed"
    @update:visible="$emit('close')"
  >
    <!-- 步骤进度 -->
    <div style="padding: 0 20px 20px;">
      <el-steps :active="stepDisplay" finish-status="success" align-center>
        <el-step :title="t" v-for="(t, i) in stepTitles" :key="i"></el-step>
      </el-steps>
    </div>

    <!-- ========== 步骤 0: 模式选择 ========== -->
    <div v-if="step === 0" style="padding: 20px 40px;">
      <h3 style="text-align:center;margin-bottom:20px;">选择模式</h3>
      <el-row :gutter="20">
        <el-col :span="12">
          <el-card shadow="hover" :class="['mode-card', { 'mode-card-active': mode === 'new' }]" @click.native="mode = 'new'">
            <i class="el-icon-plus" style="font-size:48px;color:#409EFF;"></i>
            <h4>新建表</h4>
            <p style="color:#909399;font-size:13px;">通过 AI 生成或手动编写建表 SQL，执行并拉取到平台</p>
          </el-card>
        </el-col>
        <el-col :span="12">
          <el-card shadow="hover" :class="['mode-card', { 'mode-card-active': mode === 'existing' }]" @click.native="mode = 'existing'">
            <i class="el-icon-download" style="font-size:48px;color:#67C23A;"></i>
            <h4>从数据库导入</h4>
            <p style="color:#909399;font-size:13px;">从已有数据库拉取表结构，标记字段后生成代码</p>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- ========== 步骤 1A: SQL 输入（新建表） ========== -->
    <div v-if="step === 1 && mode === 'new'" style="padding: 10px 30px;">
      <h3 style="text-align:center;margin-bottom:15px;">建表 SQL</h3>

      <!-- AI 智能生成 SQL -->
      <el-collapse v-model="aiCollapse" style="margin-bottom:15px;">
        <el-collapse-item title="AI 智能生成 SQL" name="ai">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
            <el-input v-model="aiPrompt" placeholder="描述您要生成的表，例如：创建一个用户角色关联表..." size="small" />
            <el-button type="primary" size="small" @click="handleAiGenerate" :loading="aiLoading">
              <i class="el-icon-s-promotion"></i> AI 生成
            </el-button>
          </div>
          <div style="font-size:12px;color:#909399;">
            <i class="el-icon-info"></i> AI 生成的 SQL 将直接填入下方文本框，您可以编辑后再执行
          </div>
        </el-collapse-item>
      </el-collapse>

      <el-form label-width="100px" size="small">
        <el-form-item label="数据源" :error="initialConfigId && !configDbFound ? '已配置的数据库未找到，请重新配置数据源' : ''">
          <el-select
            v-model="dataBaseId"
            placeholder="请选择数据源"
            style="width:100%;"
            filterable
            :disabled="!!initialConfigId"
            @change="onDataBaseChange"
          >
            <el-option v-for="item in dataBaseOptions" :key="item.id" :label="item.name" :value="item.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="SQL 语句">
          <el-input
            type="textarea"
            :rows="10"
            v-model="sqlText"
            placeholder="CREATE TABLE `my_table` (&#10;  `id` bigint(20) NOT NULL AUTO_INCREMENT,&#10;  `name` varchar(100) DEFAULT NULL,&#10;  PRIMARY KEY (`id`)&#10;) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4;"
            style="font-family: monospace;"
          ></el-input>
        </el-form-item>
      </el-form>
    </div>

    <!-- ========== 步骤 1B: 选择导入表（已有表模式） ========== -->
    <div v-if="step === 1 && mode === 'existing'" style="padding: 10px 30px;">
      <h3 style="text-align:center;margin-bottom:20px;">选择数据源与表</h3>
      <el-form label-width="100px" size="small">
        <el-form-item label="数据源" :error="initialConfigId && !configDbFound ? '已配置的数据库未找到，请重新配置数据源' : ''">
          <el-select
            v-model="dataBaseId"
            placeholder="请选择数据源"
            style="width:100%;"
            filterable
            :disabled="!!initialConfigId"
            @change="onDataBaseChange"
          >
            <el-option v-for="item in dataBaseOptions" :key="item.id" :label="item.name" :value="item.id" />
          </el-select>
        </el-form-item>
      </el-form>

      <div v-if="dataBaseId && !loadingTables">
        <el-button type="primary" size="small" @click="pullAndLoadTables" :loading="loadingTables" style="margin-bottom:12px;">
          <i class="el-icon-refresh"></i> 拉取表信息
        </el-button>
      </div>

      <div v-if="loadingTables" style="text-align:center;padding:20px;color:#909399;">
        <i class="el-icon-loading" style="font-size:24px;"></i>
        <p style="margin-top:8px;">正在拉取表信息...</p>
      </div>

      <div v-if="availableTables.length > 0" style="margin-top:5px;">
        <div style="max-height:240px;overflow-y:auto;margin-top:8px;">
          <el-radio-group v-model="selectedTable" @change="handleTableChange">
            <el-radio v-for="t in availableTables" :key="t" :label="t" style="display:flex;margin:6px 0;height:28px;">{{ t }}</el-radio>
          </el-radio-group>
        </div>
      </div>
      <div v-else-if="dataBaseId && !loadingTables" style="text-align:center;padding:20px;color:#C0C4CC;">
        <i class="el-icon-folder-opened"></i>
        <p style="margin-top:8px;">暂无表信息</p>
        <el-button type="primary" size="small" @click="pullAndLoadTables" :loading="loadingTables" style="margin-top:8px;">
          <i class="el-icon-refresh"></i> 从数据库拉取
        </el-button>
      </div>
    </div>

    <!-- ========== 步骤 2: 执行/拉取中（新建表） ========== -->
    <div v-if="step === 2 && mode === 'new'" style="padding: 30px;text-align:center;">
      <i class="el-icon-loading" style="font-size:36px;color:#409EFF;"></i>
      <p style="margin-top:15px;color:#909399;">正在执行 SQL 并拉取表结构...</p>
    </div>

    <!-- ========== 步骤 2 (existing 模式): 字段标记 ========== -->
    <!-- ========== 步骤 3 (new 模式): 字段标记 ========== -->
    <div v-if="(step === 2 && mode === 'existing') || (step === 3 && mode === 'new')" style="padding: 10px 30px;">
      <h3 style="text-align:center;margin-bottom:15px;">字段标记配置</h3>
      <p style="text-align:center;color:#909399;font-size:13px;margin-bottom:15px;">
        标记枚举字段和列表字段，用于代码生成时的组件渲染
      </p>

      <!-- AI 分析状态 -->
      <div v-if="fieldAiLoading" class="ai-loading-bar">
        <i class="el-icon-loading" />
        <span>AI 正在分析字段，预填标记建议…</span>
      </div>
      <!-- 空字段提示 -->
      <div v-if="!fieldAiLoading && fieldConfigs.length === 0" style="text-align:center;padding:30px;color:#C0C4CC;">
        <i class="el-icon-info" style="font-size:28px;" />
        <p>暂无字段数据</p>
        <el-button type="primary" size="small" @click="pullFieldsForCurrentTable" style="margin-top:8px;">
          <i class="el-icon-refresh" /> 从数据库拉取字段
        </el-button>
      </div>

      <template v-if="!fieldAiLoading && fieldConfigs.length > 0">
        <div style="display:flex;justify-content:flex-end;align-items:center;margin-bottom:6px;gap:4px;">
          <el-button type="text" size="mini" @click="pullFieldsForCurrentTable">
            <i class="el-icon-refresh" /> 从数据库刷新字段
          </el-button>
          <el-button type="text" size="mini" @click="runFieldAiAnalysis" :disabled="fieldAiLoading">
            <i class="el-icon-magic-stick" /> AI 智能分析
          </el-button>
        </div>

        <el-table :data="normalFields" border stripe size="small" max-height="350">
          <el-table-column label="字段名" prop="name" width="140" />
          <el-table-column label="类型" prop="fieldType" width="100" />
          <el-table-column label="注释" prop="fieldComment" min-width="120" show-overflow-tooltip />

          <el-table-column label="枚举" width="55" align="center">
            <template v-slot="scope">
              <el-checkbox v-model="scope.row.isEnum" @change="onFieldChange(scope.row)" />
            </template>
          </el-table-column>

          <el-table-column label="枚举Code" width="260">
            <template v-slot="scope">
              <div style="display:flex;gap:4px;">
                <el-select
                  v-model="scope.row.enumCode"
                  :disabled="!scope.row.isEnum"
                  size="small"
                  filterable
                  allow-create
                  clearable
                  style="flex:1;"
                  @focus="loadEnumOptions"
                >
                  <el-option v-for="item in enumOptions" :key="item.code" :label="item.code + ' (' + item.label + ')'" :value="item.code" />
                </el-select>
                <el-button size="mini" type="warning" :disabled="!scope.row.isEnum" @click="openEnumDialog(scope.row)" style="padding:6px 7px;">
                  <i class="el-icon-magic-stick"></i>
                </el-button>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="列表" width="55" align="center">
            <template v-slot="scope">
              <el-checkbox v-model="scope.row.isList" @change="onFieldChange(scope.row, 'isList')" />
            </template>
          </el-table-column>

          <el-table-column label="关联表" width="360">
            <template v-slot="scope">
              <el-select
                v-model="scope.row.listTableCode"
                :disabled="!scope.row.isList && !scope.row.isTree"
                size="small"
                filterable
                clearable
                style="width:100%;"
                @focus="loadListOptions"
                @change="(val) => onListTableChange(scope.row, val)"
              >
                <el-option v-for="item in listOptions" :key="item.tableCode" :label="'(' + item.tableCode + ') ' + (item.moduleCode ? item.moduleCode + '/' : '') + item.tableCode" :value="item.tableCode" />
              </el-select>
            </template>
          </el-table-column>

          <el-table-column label="树形" width="55" align="center">
            <template v-slot="scope">
              <el-checkbox v-model="scope.row.isTree" @change="onFieldChange(scope.row, 'isTree')" />
            </template>
          </el-table-column>

          <el-table-column label="外置" width="55" align="center">
            <template v-slot="scope">
              <el-checkbox v-model="scope.row.isTreeOut" :disabled="!scope.row.isTree" />
            </template>
          </el-table-column>
        </el-table>

        <!-- 审计字段折叠区 -->
        <div v-if="auditFields.length > 0" style="margin-top:8px;">
          <div
            style="display:flex;align-items:center;cursor:pointer;padding:6px 10px;background:#F9FAFB;border-radius:4px;user-select:none;font-size:13px;color:#909399;"
            @click="showAuditFields = !showAuditFields"
          >
            <i :class="showAuditFields ? 'el-icon-caret-bottom' : 'el-icon-caret-right'" style="margin-right:6px;"></i>
            <span>审计字段（{{ auditFields.length }} 个）</span>
            <span style="margin-left:8px;font-size:11px;color:#C0C4CC;">id / create_id / create_date / update_id / update_date / delete_flag</span>
          </div>
          <el-collapse-transition>
            <div v-show="showAuditFields" style="padding:6px 0;">
              <el-table :data="auditFields" border stripe size="small" max-height="180">
                <el-table-column label="字段名" prop="name" width="140" />
                <el-table-column label="类型" prop="fieldType" width="100" />
                <el-table-column label="注释" prop="fieldComment" min-width="120" show-overflow-tooltip />
                <el-table-column label="枚举" width="55" align="center">
                  <template><el-checkbox disabled /></template>
                </el-table-column>
                <el-table-column label="枚举Code" width="260">
                  <template><span style="color:#C0C4CC;font-size:12px;">—</span></template>
                </el-table-column>
                <el-table-column label="列表" width="55" align="center">
                  <template><el-checkbox disabled /></template>
                </el-table-column>
                <el-table-column label="关联表" width="360">
                  <template><span style="color:#C0C4CC;font-size:12px;">—</span></template>
                </el-table-column>
                <el-table-column label="树形" width="55" align="center">
                  <template><el-checkbox disabled /></template>
                </el-table-column>
                <el-table-column label="外置" width="55" align="center">
                  <template><el-checkbox disabled /></template>
                </el-table-column>
              </el-table>
            </div>
          </el-collapse-transition>
        </div>
      </template>
    </div>

    <!-- ========== 步骤 3 (existing) / 步骤 4 (new): 代码预览 ========== -->
    <div v-if="(step === 3 && mode === 'existing') || (step === 4 && mode === 'new')" style="padding: 10px 10px;">
      <h3 style="text-align:center;margin-bottom:15px;">代码预览与生成</h3>

      <div v-if="step === 3 && mode === 'existing' && pendingTableIds.length > 1" style="margin-bottom:15px;">
        <el-select v-model="currentPreviewTableId" placeholder="选择要预览的表" style="width:300px;" @change="loadCodePreview">
          <el-option v-for="tid in pendingTableIds" :key="tid" :label="tableNames[tid] || '表' + tid" :value="tid"></el-option>
        </el-select>
      </div>

      <div v-if="previewCodes.length === 0" style="text-align:center;padding:40px;color:#909399;">
        <i class="el-icon-loading" style="font-size:36px;"></i>
        <p style="margin-top:10px;">正在生成代码...</p>
      </div>
      <div v-else>
        <el-tabs type="border-card">
          <el-tab-pane v-for="item in previewCodes" :key="item.key" :label="item.key.split('/').pop()">
            <div style="color:#E6A23C;font-size:12px;margin-bottom:8px;">
              文件路径：{{ item.key.substring(0, item.key.length - item.key.split('/').pop().length - 1) }}
            </div>
            <pre style="background:#1E1E1E;color:#D4D4D4;padding:12px;border-radius:4px;max-height:400px;overflow:auto;font-size:12px;line-height:1.5;"><code>{{ item.code }}</code></pre>
          </el-tab-pane>
        </el-tabs>
        <div style="margin-top:12px;text-align:center;">
          <el-checkbox v-model="codeCheckAll" @change="handleCodeCheckAll" style="margin-right:15px;">全选</el-checkbox>
          <span style="color:#909399;font-size:12px;margin-right:15px;">已选 {{ checkedFiles.length }} / {{ previewCodes.length }} 个文件</span>
        </div>
      </div>
    </div>

    <!-- 底部 -->
    <template #footer>
      <div style="display:flex;justify-content:space-between;">
        <div>
          <el-button v-if="step > 0" @click="prevStep" size="small">
            <i class="el-icon-arrow-left"></i> 上一步
          </el-button>
        </div>
        <div>
          <el-button @click="handleCancel" size="small">取消</el-button>
          <el-button v-if="!isLastStep" type="primary" @click="nextStep" size="small" :disabled="!canNext" :loading="loading">
            {{ nextButtonText }} <i class="el-icon-arrow-right"></i>
          </el-button>
          <el-button v-if="isLastStep" type="success" @click="handleWriteCodes" size="small" :loading="loading">
            <i class="el-icon-upload2"></i> 写入磁盘
          </el-button>
        </div>
      </div>
    </template>

    <!-- ========== AI 枚举生成子对话框 ========== -->
    <el-dialog
      :visible="enumDialogVisible"
      width="700px"
      title="AI 枚举生成"
      append-to-body
      top="10vh"
      @closed="enumDialogClosed"
      @update:visible="enumDialogVisible = $event"
    >
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;">
        <el-input v-model="enumPrompt" placeholder="描述枚举用途，如：用户状态枚举（启用/禁用/冻结）" size="small" />
        <el-button type="primary" size="small" @click="handleAiEnumGenerate" :loading="enumLoading">
          <i class="el-icon-s-promotion"></i> 生成
        </el-button>
      </div>

      <div v-if="enumGeneratedCode" style="margin-top:10px;">
        <pre style="background:#1E1E1E;color:#D4D4D4;padding:12px;border-radius:4px;max-height:300px;overflow:auto;font-size:12px;line-height:1.5;"><code>{{ enumGeneratedCode }}</code></pre>

        <div style="margin-top:12px;display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
          <el-input v-model="enumFilePath" size="small" placeholder="存放路径（相对于模块根目录）" style="flex:2;min-width:200px;" />
          <el-input v-model="enumFileName" size="small" placeholder="文件名.java" style="flex:1;min-width:140px;" />
          <el-button type="success" size="small" @click="handleEnumWriteFile" :disabled="!enumGeneratedCode || !enumFileName">
            <i class="el-icon-upload2"></i> 写入磁盘
          </el-button>
        </div>
      </div>
      <div v-else style="text-align:center;padding:30px;color:#909399;font-size:13px;">
        <i class="el-icon-info"></i> 输入枚举描述后点击「生成」按钮，AI 将按项目规范生成枚举类代码
      </div>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { getList, getListById, getInfoById } from "@/api/baseApi";
import { codePreview, pullDataTable, pullDataField, executeTableSql, getProjectModelEnumCodes, getProjectModelListCodes, getProjectModelTreeCodes, batchUpdateField, getTableListByPrefix } from "@/api/dataApi";
import baseUrl from "@/config/baseUrl";

export default {
  name: "SmartTableWizard",
  props: {
    visible: Boolean,
    moduleId: { type: [Number, String], default: null },
    moduleName: { type: String, default: "" },
    projectId: { type: [Number, String], default: null },
    initialConfigId: { type: [Number, String], default: null },
    prefix: { type: String, default: '' }
  },
  data() {
    return {
      step: 0,
      mode: null,
      loading: false,
      dataBaseOptions: [],
      dataBaseId: null,
      // new mode
      sqlText: "",
      // existing mode
      availableTables: [],
      selectedTable: null,
      loadingTables: false,
      // shared
      fieldConfigs: [],
      currentTableId: null,
      pendingTableIds: [],
      currentPreviewTableId: null,
      tableNames: {},
      previewCodes: [],
      checkedFiles: [],
      codeCheckAll: false,
      // AI SQL generation
      aiCollapse: "",
      aiPrompt: "",
      aiLoading: false,
      aiEventSource: null,
      // table info (for AI field analysis)
      tableName: "",
      tableComment: "",
      // field AI analysis
      fieldAiLoading: false,
      _fieldAiAbort: null,
      enumOptions: [],
      listOptions: [],
      _enumOptionsLoaded: false,
      _listOptionsLoaded: false,
      // enum generation dialog
      enumDialogVisible: false,
      enumDialogRow: null,
      enumPrompt: "",
      enumLoading: false,
      enumEventSource: null,
      enumGeneratedCode: "",
      enumFilePath: "",
      enumFileName: "",
      // 审计字段折叠
      showAuditFields: false,
      _originalFieldTags: null
    };
  },
  computed: {
    auditFieldNames() {
      return ['id', 'create_id', 'create_org_id', 'create_date', 'update_id', 'update_date', 'delete_flag'];
    },
    normalFields() {
      return this.fieldConfigs.filter(f => !this.auditFieldNames.includes(f.name));
    },
    auditFields() {
      return this.fieldConfigs.filter(f => this.auditFieldNames.includes(f.name));
    },
    stepTitles() {
      if (this.mode === "new") {
        return ["选择模式", "编写 SQL", "执行并拉取", "字段标记", "代码预览"];
      }
      return ["选择模式", "选择表", "字段标记", "代码预览"];
    },
    stepDisplay() {
      if (this.mode === "new") return this.step;
      const map = { 0: 0, 1: 1, 2: 2, 3: 3 };
      return map[this.step] !== undefined ? map[this.step] : this.step;
    },
    isLastStep() {
      if (this.mode === "new") return this.step === 4;
      return this.step === 3;
    },
    canNext() {
      if (this.step === 0) return !!this.mode;
      if (this.step === 1 && this.mode === "new") return !!this.sqlText.trim() && !!this.dataBaseId;
      if (this.step === 1 && this.mode === "existing") return !!this.selectedTable;
      return true;
    },
    nextButtonText() {
      if (this.step === 1 && this.mode === "new") return "执行 SQL";
      if (this.step === 1 && this.mode === "existing") return "拉取并继续";
      return "下一步";
    },
    configDbFound() {
      if (!this.initialConfigId) return true;
      return this.dataBaseOptions.some(d => String(d.id) === String(this.initialConfigId));
    }
  },
  watch: {
    visible(val) {
      if (val) this.loadDataBaseOptions();
    }
  },
  methods: {
    async loadDataBaseOptions() {
      try {
        const res = await getList("data", "dataBase");
        this.dataBaseOptions = res.data || [];
        if (this.initialConfigId) {
          this.dataBaseId = this.initialConfigId;
          // 自动加载已存在的表列表
          await this.onDataBaseChange();
        }
      } catch (e) {
        console.error("加载数据库列表失败:", e);
      }
    },
    async onDataBaseChange() {
      this.availableTables = [];
      this.selectedTable = null;
      if (this.dataBaseId) {
        await this.loadExistingTables();
      }
    },
    async loadExistingTables() {
      this.loadingTables = true;
      try {
        const res = await getTableListByPrefix(this.dataBaseId, this.prefix);
        this.availableTables = (res.data || []).map(t => t.name);
      } catch (e) {
        console.error("加载已有表失败:", e);
      } finally {
        this.loadingTables = false;
      }
    },
    prevStep() {
      if (this.step <= 0) return;
      this.step--;
    },
    async nextStep() {
      if (this.step === 1 && this.mode === "new") {
        await this.handleExecuteSql();
        return;
      }
      if (this.step === 1 && this.mode === "existing") {
        await this.handleImportTables();
        return;
      }
      if (this.isTagStep()) {
        await this.saveFieldTags();
        this.step++;
        this.loadCodePreview();
        return;
      }
      this.step++;
    },
    isTagStep() {
      if (this.mode === "new") return this.step === 3;
      return this.step === 2;
    },

    /* ---------- 新建表：执行 SQL ---------- */
    async handleExecuteSql() {
      this.loading = true;
      try {
        const res = await executeTableSql({
          dataBaseId: this.dataBaseId,
          sql: this.sqlText
        });
        if (res.code === 200) {
          this.$message.success("SQL 执行成功");
          // 自动拉取表
          await pullDataTable(this.dataBaseId);
          // 查询拉取到的表列表，取最新的那个
          const listRes = await getList("data", "table");
          const tables = (listRes.data || []).filter(t => t.dataBaseId === this.dataBaseId);
          if (tables.length > 0) {
            // 取最新的表（按id排序取最后一个）
            const newTable = tables.sort((a, b) => b.id - a.id)[0];
            this.currentTableId = newTable.id;
            await this.loadFieldConfigs(newTable.id);
            this.step = 3;
            this.$nextTick(() => this.runFieldAiAnalysis());
          } else {
            // 没有拉取到表，重新拉取一次
            await pullDataTable(this.dataBaseId);
            const retryRes = await getList("data", "table");
            const retryTables = (retryRes.data || []).filter(t => t.dataBaseId === this.dataBaseId);
            if (retryTables.length > 0) {
              const newTable = retryTables.sort((a, b) => b.id - a.id)[0];
              this.currentTableId = newTable.id;
              await this.loadFieldConfigs(newTable.id);
              this.step = 3;
              this.$nextTick(() => this.runFieldAiAnalysis());
            } else {
              this.$message.warning("SQL 已执行，但未拉取到新表，请手动拉取");
              this.step = 2;
            }
          }
        } else {
          this.$message.error(res.msg || "SQL 执行失败");
        }
      } catch (e) {
        console.error("执行SQL失败:", e);
        this.$message.error("SQL 执行失败: " + (e.message || ""));
      } finally {
        this.loading = false;
      }
    },

    /* ---------- 导入表 ---------- */
    async pullAndLoadTables() {
      this.loadingTables = true;
      try {
        await pullDataTable(this.dataBaseId);
        this.$message.success("拉取成功");
        await this.loadExistingTables();
      } catch (e) {
        console.error("拉取表信息失败:", e);
        this.$message.error("拉取表信息失败");
      } finally {
        this.loadingTables = false;
      }
    },
    handleTableChange(val) {
      // 单选，仅记录选中值
    },
    async handleImportTables() {
      this.loading = true;
      try {
        // 拉取所有选中表的字段
        const listRes = await getList("data", "table");
        const allTables = listRes.data || [];
        const tableIds = [];

        const tableName = this.selectedTable;
        if (tableName) {
          const table = allTables.find(t => t.name === tableName && t.dataBaseId === this.dataBaseId);
          if (table) {
            tableIds.push(table.id);
            this.$set(this.tableNames, table.id, table.name);
          }
        }

        this.pendingTableIds = tableIds;
        this.currentPreviewTableId = tableIds[0] || null;

        // 加载字段供标记
        if (tableIds.length > 0) {
          this.currentTableId = tableIds[0];
          await this.loadFieldConfigs(tableIds[0]);
        }

        this.step = 2;
        this.$nextTick(() => this.runFieldAiAnalysis());
        this.$message.success(`成功导入表 ${tableName}`);
      } catch (e) {
        console.error("导入表失败:", e);
        this.$message.error("导入表失败");
      } finally {
        this.loading = false;
      }
    },

    /* ---------- 字段标记 ---------- */
    async loadFieldConfigs(tableId) {
      try {
        const [fieldRes, tableRes] = await Promise.all([
          getListById("data", "field", tableId),
          getInfoById("data", "table", tableId)
        ]);
        const fields = fieldRes.data || [];
        this.fieldConfigs = fields.map(f => ({
          id: f.id,
          tableId: f.tableId,
          name: f.name,
          fieldType: f.fieldType || f.dataType || "",
          fieldComment: f.fieldComment || "",
          isEnum: f.isEnum === "YES" || f.isEnum === true || f.isEnum === 1,
          enumCode: f.enumCode || "",
          isList: f.isList === "YES" || f.isList === true || f.isList === 1,
          isTree: f.isTree === "YES" || f.isTree === true || f.isTree === 1,
          isTreeOut: f.isTreeOut === "YES" || f.isTreeOut === true || f.isTreeOut === 1,
          listTableCode: f.listTableCode || "",
          listModuleCode: f.listModuleCode || ""
        }));
        if (tableRes.code === 200 && tableRes.data) {
          this.tableName = tableRes.data.name || "";
          this.tableComment = tableRes.data.annotation || "";
        }
        // 保存原始值快照，用于对比变化
        this._originalFieldTags = fields.map(f => ({
          id: f.id,
          tableId: f.tableId,
          isEnum: f.isEnum === "YES" || f.isEnum === true || f.isEnum === 1,
          enumCode: f.enumCode || "",
          isList: f.isList === "YES" || f.isList === true || f.isList === 1,
          listTableCode: f.listTableCode || "",
          listModuleCode: f.listModuleCode || "",
          isTree: f.isTree === "YES" || f.isTree === true || f.isTree === 1,
          isTreeOut: f.isTreeOut === "YES" || f.isTreeOut === true || f.isTreeOut === 1
        }));
        // 预加载枚举和关联表选项，确保 el-select 能匹配到格式化标签
        this.loadEnumOptions();
        // 必须 await，确保 _resolveListModuleCodes() 在可保存前已将旧的 listModuleCode 覆盖为正确值
        await this.loadListOptions();
      } catch (e) {
        console.error("加载字段配置失败:", e);
      }
    },
    async pullFieldsForCurrentTable() {
      if (!this.currentTableId) return;
      this.loading = true;
      try {
        await pullDataField(this.currentTableId);
        this.$message.success("字段拉取成功");
        await this.loadFieldConfigs(this.currentTableId);
        this.$nextTick(() => this.runFieldAiAnalysis());
      } catch (e) {
        console.error("拉取字段失败:", e);
        this.$message.error("拉取字段失败");
      } finally {
        this.loading = false;
      }
    },
    onFieldChange(row, field) {
      if (!row.isEnum) row.enumCode = "";
      // 列表和树形互斥
      if (field === 'isList' && row.isList && row.isTree) row.isTree = false;
      if (field === 'isTree' && row.isTree && row.isList) row.isList = false;
      if (!row.isList && !row.isTree) {
        row.listTableCode = "";
        row.listModuleCode = "";
      }
      if (!row.isTree) row.isTreeOut = false;
    },
    onListTableChange(row, val) {
      if (val) {
        const option = this.listOptions.find(o => o.tableCode === val);
        if (option) row.listModuleCode = option.moduleCode;
      } else {
        row.listModuleCode = "";
      }
    },

    /* ---------- 字段 AI 分析（fetch + JSON，OpenAI 标准） ---------- */
    async runFieldAiAnalysis() {
      if (this.fieldConfigs.length === 0) return;
      this.fieldAiLoading = true;
      this._cleanupFieldAi();

      try {
        await Promise.all([this.loadEnumOptions(), this.loadListOptions()]);

        const prompt = this._buildFieldAiPrompt();
        const config = this.$ai || {};
        const endpoint = config.endpoint || "/v1/chat/completions";
        const model = config.model || "ernie-4.0";
        const apiKey = config.apiKey || "";

        const headers = { "Content-Type": "application/json" };
        if (apiKey) { headers["Authorization"] = "Bearer " + apiKey; }

        const controller = new AbortController();
        this._fieldAiAbort = controller;

        const response = await fetch(endpoint, {
          method: "POST",
          headers,
          signal: controller.signal,
          body: JSON.stringify({
            model,
            messages: [
              {
                role: "system",
                content: "你是一个数据库字段智能分析助手。根据输入的表信息和字段列表，分析每个字段并建议标记。" +
                  "规则：1.审计字段（create_id, create_org_id, create_date, update_id, update_date, delete_flag）后端自动处理，不分析" +
                  "2.枚举(isEnum): 字段名以 _status/_type/_state/_level 结尾，或注释含状态/类型/等级/是否等关键词。state固定填state" +
                  "3.列表(isList): 字段名以 _id 结尾的外键字段（不含id/parent_id及审计字段），匹配可用关联表填入listTableCode" +
                  "4.树形(isTree): 同列表，但目标关联表是树形结构表（目标表中有parent_id字段），用于前端渲染树形视图。与列表互斥，需同时填入listTableCode" +
                  "5.外置树(isTreeOut): 仅当isTree=true时生效。分析业务场景判断：外置树(true)适用于分类管理场景（组织架构、产品分类、地区层级等），页面左侧单独树面板，点击节点过滤列表；内置树(false)适用于普通筛选场景，检索区使用树形下拉框。无法确定时默认false。" +
                  "严格按JSON格式输出：{\"fields\":[{\"name\":\"字段名\",\"isEnum\":true/false,\"enumCode\":\"枚举代码\",\"isList\":true/false,\"listTableCode\":\"表代码\",\"isTree\":true/false,\"isTreeOut\":true/false}]}，不要markdown代码块。"
              },
              { role: "user", content: prompt }
            ],
            stream: false
          })
        });

        if (!response.ok) throw new Error("HTTP " + response.status);

        const result = await response.json();
        if (result.code === 200 && result.data) {
          this._applyFieldAiResult(result.data);
        } else {
          throw new Error(result.message || "AI 分析失败");
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("AI 字段分析失败:", e);
          this.$message.warning("AI 分析失败，请手动标记");
        }
        this.fieldAiLoading = false;
        this._fieldAiAbort = null;
      }
    },

    _buildFieldAiPrompt() {
      let prompt = "表单上下文:\n表名: " + this.tableName + "\n表注释: " + this.tableComment + "\n";
      if (this.mode === "new" && this.sqlText) {
        prompt += "原始建表SQL: " + this.sqlText + "\n";
      }
      if (this.enumOptions.length > 0) {
        prompt += "可用枚举: ";
        this.enumOptions.forEach(e => { prompt += e.code + "(" + e.label + "), "; });
        prompt += "\n";
      }
      if (this.listOptions.length > 0) {
        prompt += "可用关联表: ";
        this.listOptions.forEach(t => {
          prompt += (t.moduleCode ? t.moduleCode + "/" : "") + t.tableCode + "(" + (t.tableName || "") + "), ";
        });
        prompt += "\n";
      }
      prompt += "\n表单字段:\n";
      this.fieldConfigs.forEach((f) => {
        if (this.auditFieldNames.includes(f.name)) return;
        prompt += "- " + f.name + " (类型: " + f.fieldType + ", 注释: " + (f.fieldComment || "无") + ")\n";
      });
      prompt += "\n请推断每个字段的：isEnum(true/false), enumCode(匹配可用枚举), isList(true/false), listTableCode(匹配可用关联表), isTree(true/false)";
      return prompt;
    },

    _applyFieldAiResult(responseText) {
      this._cleanupFieldAi();
      this.fieldAiLoading = false;
      try {
        let jsonStr = responseText.trim();
        const m = jsonStr.match(/```(?:json)?\s*\n?([\s\S]*?)\n?```/);
        if (m) jsonStr = m[1];
        const result = JSON.parse(jsonStr);
        const fields = Array.isArray(result.fields) ? result.fields : [];
        if (fields.length > 0) {
          let count = 0;
          for (const s of fields) {
            const field = this.fieldConfigs.find(f => f.name === s.name);
            if (!field) continue;
            if (s.isEnum && !field.isEnum) { field.isEnum = true; count++; }
            if (s.enumCode && !field.enumCode) { field.enumCode = s.enumCode; count++; }
            if (s.isList && !field.isList) { field.isList = true; count++; }
            if (s.listTableCode && !field.listTableCode) { field.listTableCode = s.listTableCode; count++; }
            if (s.isTree && !field.isTree) { field.isTree = true; count++; }
            if (typeof s.isTreeOut === "boolean" && s.isTreeOut && !field.isTreeOut) { field.isTreeOut = true; count++; }
          }
          // AI 可能同时标记了列表和树形，互斥处理
          this.fieldConfigs.forEach(fc => {
            if (fc.isList && fc.isTree) {
              fc.isList = false;
              if (!fc.listTableCode) {
                const match = this.listOptions.find(o => o.tableCode === fc.listTableCode);
                if (match) fc.listTableCode = match.tableCode || '';
              }
            }
          });
          this._resolveListModuleCodes();
          if (count > 0) {
            this.$message.success("AI 已预填 " + count + " 项标记");
          }
        }
      } catch (e) {
        console.error("解析 AI 字段分析结果失败:", e);
      }
    },

    /** 从 listOptions 同步字段的 listModuleCode（始终覆盖，纠正扫描/旧数据中的错误值） */
    _resolveListModuleCodes() {
      this.fieldConfigs.forEach(fc => {
        if (fc.listTableCode) {
          const match = this.listOptions.find(o => o.tableCode === fc.listTableCode);
          if (match && match.moduleCode) fc.listModuleCode = match.moduleCode;
        }
      });
    },

    _cleanupFieldAi() {
      if (this._fieldAiAbort) {
        this._fieldAiAbort.abort();
        this._fieldAiAbort = null;
      }
    },

    async loadEnumOptions() {
      if (this._enumOptionsLoaded) return;
      try {
        if (!this.projectId) { this._enumOptionsLoaded = true; return; }
        const res = await getProjectModelEnumCodes(this.projectId);
        this.enumOptions = (res.data || []).map(item => ({
          code: item.enumCode || item.code || '',
          label: item.enumLabel || item.simpleName || item.code || ''
        })).filter(o => o.code);
        this._enumOptionsLoaded = true;
      } catch (e) {
        console.error("加载枚举选项失败:", e);
      }
    },

    async loadListOptions() {
      if (this._listOptionsLoaded) return;
      try {
        if (!this.projectId) { this._listOptionsLoaded = true; return; }
        const [listRes, treeRes] = await Promise.all([
          getProjectModelListCodes(this.projectId),
          getProjectModelTreeCodes(this.projectId)
        ]);
        const seen = new Set();
        this.listOptions = [];
        [...(listRes.data || []), ...(treeRes.data || [])].forEach(item => {
          // code 可能包含 moduleName 前缀（如 process-domain/system/organization），取最后一段
          const rawCode = item.code;
          const code = rawCode ? rawCode.split('/').pop() : '';
          if (code && !seen.has(code)) {
            seen.add(code);
            // 模块code优先级: listModuleCode(权限码) > URL路径提取 > moduleName(备用)
            let restModule = item.listModuleCode || '';
            if (!restModule) {
              const apiPath = item.listApiPath || '';
              if (apiPath && code) {
                const parts = apiPath.split('/').filter(Boolean);
                const codeIdx = parts.lastIndexOf(code);
                if (codeIdx > 0) {
                  restModule = parts[codeIdx - 1];
                }
              }
            }
            if (!restModule) {
              restModule = (item.moduleName || '').split('/').pop();
            }
            this.listOptions.push({
              tableCode: code,
              tableName: item.description || item.simpleName || '',
              moduleCode: restModule
            });
          }
        });
        this._resolveListModuleCodes();
        this._listOptionsLoaded = true;
      } catch (e) {
        console.error("加载列表/树形码失败:", e);
      }
    },

    async saveFieldTags() {
      // 构建原始值 map
      const originMap = {};
      (this._originalFieldTags || []).forEach(o => { originMap[o.id] = o; });
      // 筛选出有变化的字段
      const changed = this.fieldConfigs
        .filter(fc => fc.id)
        .filter(fc => {
          const orig = originMap[fc.id];
          if (!orig) return true;
          return orig.isEnum !== fc.isEnum
            || orig.enumCode !== (fc.enumCode || '')
            || orig.isList !== fc.isList
            || orig.listTableCode !== (fc.listTableCode || '')
            || orig.listModuleCode !== (fc.listModuleCode || '')
            || orig.isTree !== fc.isTree
            || orig.isTreeOut !== fc.isTreeOut;
        })
        .map(fc => ({
          id: fc.id,
          tableId: fc.tableId,
          isEnum: fc.isEnum ? 1 : 0,
          enumCode: fc.enumCode || "",
          isList: fc.isList ? 1 : 0,
          listTableCode: fc.listTableCode || "",
          listModuleCode: fc.listModuleCode || "",
          isTree: fc.isTree ? 1 : 0,
          isTreeOut: fc.isTreeOut ? 1 : 0
        }));
      if (changed.length === 0) return;
      try {
        await batchUpdateField(changed);
        // 更新原始值快照
        this._originalFieldTags = this.fieldConfigs
          .filter(fc => fc.id)
          .map(fc => ({
            id: fc.id,
            tableId: fc.tableId,
            isEnum: fc.isEnum,
            enumCode: fc.enumCode || '',
            isList: fc.isList,
            listTableCode: fc.listTableCode || '',
            listModuleCode: fc.listModuleCode || '',
            isTree: fc.isTree,
            isTreeOut: fc.isTreeOut
          }));
      } catch (e) {
        console.error("批量保存字段标记失败:", e);
        this.$message.error("字段标记保存失败");
      }
    },

    /* ---------- 代码预览 ---------- */
    async loadCodePreview() {
      const tableId = this.currentPreviewTableId || this.currentTableId;
      if (!tableId) return;
      try {
        const moduleId = this.moduleId;
        const res = await codePreview(tableId, moduleId);
        if (res.code === 200) {
          this.previewCodes = res.data || [];
          this.checkedFiles = this.previewCodes.map(item => item.key.split("/").pop());
          this.codeCheckAll = true;
        }
      } catch (e) {
        console.error("代码预览失败:", e);
        this.$message.error("代码预览失败");
      }
    },
    handleCodeCheckAll(val) {
      this.checkedFiles = val ? this.previewCodes.map(item => item.key.split("/").pop()) : [];
    },
    async handleWriteCodes() {
      this.loading = true;
      try {
        const isElectron = navigator.userAgent.toLowerCase().indexOf("electron/") !== -1;
        if (isElectron) {
          for (const item of this.previewCodes) {
            const fileName = item.key.split("/").pop();
            if (!this.checkedFiles.includes(fileName)) continue;
            const filePath = item.key.substring(0, item.key.length - fileName.length - 1);
            window.appApi.writeFile({ fileName, path: filePath, data: item.code });
          }
          this.$message.success("代码生成成功！");
        } else if (window.showDirectoryPicker) {
          const rootDir = await window.showDirectoryPicker({ mode: "readwrite" });
          let count = 0;
          for (const item of this.previewCodes) {
            const fileName = item.key.split("/").pop();
            if (!this.checkedFiles.includes(fileName)) continue;
            const dir = item.key.substring(0, item.key.length - fileName.length - 1);
            const parts = dir ? dir.split("/").filter(p => p) : [];
            let current = rootDir;
            for (const p of parts) current = await current.getDirectoryHandle(p, { create: true });
            const fh = await current.getFileHandle(fileName, { create: true });
            const w = await fh.createWritable();
            await w.write(item.code);
            await w.close();
            count++;
          }
          this.$message.success(`代码生成成功！共 ${count} 个文件`);
        } else {
          this.$message.warning("当前环境不支持文件写入，请手动复制代码");
        }
        this.$emit("close");
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("写入代码失败:", e);
          this.$message.error("写入代码失败");
        }
      } finally {
        this.loading = false;
      }
    },
    handleCancel() {
      this.$emit("close");
    },

    /* ---------- AI SQL 生成 ---------- */
    handleAiGenerate() {
      if (!this.aiPrompt.trim()) {
        this.$message.warning("请输入表描述");
        return;
      }
      if (this.aiEventSource) { this.aiEventSource.close(); }
      this.aiLoading = true;
      this.aiEventSource = new EventSource(baseUrl + 'data/llm/sql?prompt=' + encodeURIComponent(this.aiPrompt));
      this.sqlText = "";
      this.aiEventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.choices && data.choices[0] && data.choices[0].delta) {
            this.sqlText += data.choices[0].delta.content || "";
          }
        } catch (e) {
          this.sqlText += event.data;
        }
      };
      this.aiEventSource.onerror = () => {
        this.aiLoading = false;
        this.aiEventSource.close();
        this.aiEventSource = null;
      };
      this.aiEventSource.addEventListener('end', () => {
        this.aiLoading = false;
        this.aiEventSource.close();
        this.aiEventSource = null;
      });
    },


    /* ---------- AI 枚举生成 ---------- */
    openEnumDialog(row) {
      this.enumDialogRow = row;
      this.enumPrompt = "";
      this.enumGeneratedCode = "";
      this.enumFilePath = "src/main/java/com/yida/enums/";
      this.enumFileName = "";
      this.enumDialogVisible = true;
    },
    handleAiEnumGenerate() {
      if (!this.enumPrompt.trim()) {
        this.$message.warning("请输入枚举描述");
        return;
      }
      if (this.enumEventSource) { this.enumEventSource.close(); }
      this.enumLoading = true;
      this.enumGeneratedCode = "";
      this.enumEventSource = new EventSource(baseUrl + 'data/llm/enum?prompt=' + encodeURIComponent(this.enumPrompt));
      this.enumEventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.choices && data.choices[0] && data.choices[0].delta) {
            this.enumGeneratedCode += data.choices[0].delta.content || "";
          }
        } catch (e) {
          this.enumGeneratedCode += event.data;
        }
      };
      this.enumEventSource.onerror = () => {
        this.enumLoading = false;
        this.enumEventSource.close();
        this.enumEventSource = null;
      };
      this.enumEventSource.addEventListener('end', () => {
        this.enumLoading = false;
        this.enumEventSource.close();
        this.enumEventSource = null;
      });
    },
    async handleEnumWriteFile() {
      if (!this.enumGeneratedCode || !this.enumFileName) return;
      const isElectron = navigator.userAgent.toLowerCase().indexOf("electron/") !== -1;
      try {
        if (isElectron) {
          window.appApi.writeFile({
            fileName: this.enumFileName,
            path: this.enumFilePath,
            data: this.enumGeneratedCode
          });
          this.$message.success("枚举文件生成成功！");
          this.enumDialogVisible = false;
        } else if (window.showDirectoryPicker) {
          const rootDir = await window.showDirectoryPicker({ mode: "readwrite" });
          const parts = this.enumFilePath ? this.enumFilePath.split("/").filter(p => p) : [];
          let current = rootDir;
          for (const p of parts) current = await current.getDirectoryHandle(p, { create: true });
          const fh = await current.getFileHandle(this.enumFileName, { create: true });
          const w = await fh.createWritable();
          await w.write(this.enumGeneratedCode);
          await w.close();
          this.$message.success("枚举文件生成成功！");
          this.enumDialogVisible = false;
        } else {
          this.$message.warning("当前环境不支持文件写入，请手动复制代码");
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("写入枚举文件失败:", e);
          this.$message.error("写入枚举文件失败");
        }
      }
    },
    enumDialogClosed() {
      this.enumDialogRow = null;
      this.enumPrompt = "";
      this.enumLoading = false;
      if (this.enumEventSource) { this.enumEventSource.close(); this.enumEventSource = null; }
      this.enumGeneratedCode = "";
      this.enumFilePath = "";
      this.enumFileName = "";
    },
    handleClosed() {
      this.step = 0;
      this.mode = null;
      this.loading = false;
      this.sqlText = "";
      this.dataBaseId = null;
      this.availableTables = [];
      this.selectedTable = null;
      this.fieldConfigs = [];
      this._originalFieldTags = null;
      this.currentTableId = null;
      this.pendingTableIds = [];
      this.currentPreviewTableId = null;
      this.tableNames = {};
      this.previewCodes = [];
      this.checkedFiles = [];
      this.codeCheckAll = false;
      this.aiCollapse = "";
      this.aiPrompt = "";
      this.aiLoading = false;
      if (this.aiEventSource) { this.aiEventSource.close(); this.aiEventSource = null; }
      this.tableName = "";
      this.tableComment = "";
      this.fieldAiLoading = false;
      this._cleanupFieldAi();
      this.enumOptions = [];
      this.listOptions = [];
      this._enumOptionsLoaded = false;
      this._listOptionsLoaded = false;
      this.enumDialogVisible = false;
      this.enumDialogRow = null;
      this.enumPrompt = "";
      this.enumLoading = false;
      if (this.enumEventSource) { this.enumEventSource.close(); this.enumEventSource = null; }
      this.enumGeneratedCode = "";
      this.enumFilePath = "";
      this.enumFileName = "";
      this.showAuditFields = false;
    }
  }
};
</script>

<style scoped>
.mode-card {
  cursor: pointer;
  text-align: center;
  padding: 10px;
  transition: all 0.2s;
  border: 2px solid transparent;
}
.mode-card:hover { border-color: #409EFF; }
.mode-card-active { border-color: #409EFF; background: #ECF5FF; }
.mode-card h4 { margin: 12px 0 8px; }

.ai-loading-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  background: #ECF5FF;
  border: 1px solid #D9ECFF;
  border-radius: 6px;
  color: #409EFF;
  font-size: 14px;
  margin-bottom: 12px;
}
.ai-loading-bar i {
  font-size: 20px;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
